export const PICKUP_TIMEZONE = "America/Los_Angeles";

/** Minimum lead time in minutes between "now" and a bookable pickup slot. */
export const PICKUP_MIN_NOTICE_MINUTES = 60;

// Universal slot list, 30-min increments from 10:00 to 18:00. Earlier
// slots (10:00-13:30) were added so dropoffs can be booked in the
// morning. Per-day return restrictions (e.g.
// Thursday's driver-in-class 3-PM minimum for returns) are layered on
// top via getDropoffTimeSlotsForDate in laundry.ts.
//
// Recurring customer pickups start at 18:00 — see
// CUSTOMER_MIN_PICKUP_TIME below. Admins get the broader ADMIN_TIME_SLOTS
// universe (7 AM-10 PM) so they can override anything the customer surface
// can't reach.
export const PICKUP_TIME_SLOTS = [
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
  "17:30",
  "18:00",
] as const;

/**
 * Earliest time a customer can pick for a pickup. Per-day schedule entries
 * explicitly override this default. The shared schedule loader filters generated
 * Tuesdays to this floor; custom dates expose exactly the configured times.
 */
export const CUSTOMER_MIN_PICKUP_TIME = "18:00";
/** Recurring pickup run: Tuesday, 6–7 PM. Explicit admin dates can override it. */
export const DEFAULT_PICKUP_TIME_SLOTS = ["18:00"] as const;

/** Latest LA local time a customer may SUBMIT a last-minute request. */
export const LAST_MINUTE_REQUEST_CUTOFF = "19:30";

/**
 * Wider slot universe for admin pickers. Covers 7 AM-10 PM in 30-min
 * increments so admins can schedule one-off early-morning or evening
 * pickups / dropoffs that the customer flow doesn't expose.
 */
export const ADMIN_TIME_SLOTS = [
  "07:00", "07:30", "08:00", "08:30", "09:00", "09:30",
  "10:00", "10:30", "11:00", "11:30", "12:00", "12:30",
  "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00", "17:30", "18:00", "18:30",
  "19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00",
] as const;

/** A pickup date entry - from the DB schedule or generated as a default Tuesday */
export type PickupScheduleEntry = {
  date: string;         // "YYYY-MM-DD"
  time_slots: string[]; // ["14:00", "14:30", ...]
  label?: string | null;
  /** Pickup window length (60/90/120) snapshotted onto bookings for this date. */
  window_minutes?: number;
};

const WEEKDAY_SHORT_TO_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const LA_PARTS_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: PICKUP_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

type IsoDateParts = {
  year: number;
  month: number;
  day: number;
};

const parseIsoDateParts = (value: string): IsoDateParts | null => {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const probe = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
};

const formatIsoDate = ({ year, month, day }: IsoDateParts) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

export const formatIsoDateString = (date: Date) =>
  formatIsoDate({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() });

export const addDaysToIsoDate = (
  value: string,
  days: number
): string | null => {
  const parts = parseIsoDateParts(value);
  if (!parts) return null;
  const probe = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0));
  probe.setUTCDate(probe.getUTCDate() + days);
  return formatIsoDate({
    year: probe.getUTCFullYear(),
    month: probe.getUTCMonth() + 1,
    day: probe.getUTCDate(),
  });
};

export const getIsoWeekday = (value: string): number | null => {
  const parts = parseIsoDateParts(value);
  if (!parts) return null;
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0)).getUTCDay();
};

export const parseTimeToMinutes = (value?: string | null): number | null => {
  if (!value) return null;
  const match = String(value).trim().match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
};

type LosAngelesDateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
  isoDate: string;
  minutes: number;
};

const getLosAngelesDateTimeParts = (
  date: Date
): LosAngelesDateTimeParts | null => {
  const parts = LA_PARTS_FORMATTER.formatToParts(date);
  const year = Number(parts.find((part) => part.type === "year")?.value ?? "");
  const month = Number(parts.find((part) => part.type === "month")?.value ?? "");
  const day = Number(parts.find((part) => part.type === "day")?.value ?? "");
  const hour = Number(parts.find((part) => part.type === "hour")?.value ?? "");
  const minute = Number(parts.find((part) => part.type === "minute")?.value ?? "");
  const weekdayShort = parts.find((part) => part.type === "weekday")?.value ?? "";
  const weekday = WEEKDAY_SHORT_TO_INDEX[weekdayShort];
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    !Number.isFinite(weekday)
  ) {
    return null;
  }
  return {
    year,
    month,
    day,
    hour,
    minute,
    weekday,
    isoDate: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
    minutes: hour * 60 + minute,
  };
};

export const getLosAngelesNowInfo = (now = new Date()) =>
  getLosAngelesDateTimeParts(now);

/** The weekday pickups run on (LA). 2 = Tuesday. */
export const PICKUP_WEEKDAY = 2;

/**
 * True when today (LA) is a pickup day. Last-minute is a same-day request, so
 * it only makes sense on a day a runner is already out. Pickups run Tuesdays
 * at this point; this mirrors the Tuesday-only fallback used elsewhere.
 */
export const isPickupDayInLosAngeles = (now = new Date()): boolean => {
  const info = getLosAngelesNowInfo(now);
  if (!info) return false;
  return info.weekday === PICKUP_WEEKDAY;
};

/**
 * True when a last-minute pickup can still be requested right now: today (LA) is
 * a pickup day AND the local time is before LAST_MINUTE_REQUEST_CUTOFF (7:30 PM).
 * Last-minute requests are same-day, open-window, and bypass the
 * floor/ceiling/notice rules.
 */
export const isLastMinuteWindowOpenInLosAngeles = (now = new Date()): boolean => {
  const info = getLosAngelesNowInfo(now);
  if (!info) return false;
  if (info.weekday !== PICKUP_WEEKDAY) return false;
  const cutoff = parseTimeToMinutes(LAST_MINUTE_REQUEST_CUTOFF);
  if (cutoff === null) return false;
  return info.minutes < cutoff;
};

/**
 * The date/time to STORE for a last-minute request: today (LA) and the current
 * LA time as HH:MM. Server-authoritative, never trust a client-sent slot for
 * last-minute. Returns null if LA time can't be resolved.
 */
export const getLastMinutePickupSlotInLosAngeles = (
  now = new Date(),
): { date: string; time: string } | null => {
  const info = getLosAngelesNowInfo(now);
  if (!info) return null;
  const time = `${String(info.hour).padStart(2, "0")}:${String(info.minute).padStart(2, "0")}`;
  return { date: info.isoDate, time };
};

export const toLosAngelesDateTime = (
  preferredDate?: string | null,
  preferredTime?: string | null
): Date | null => {
  if (!preferredDate || !preferredTime) return null;
  const dateParts = parseIsoDateParts(preferredDate);
  const minutes = parseTimeToMinutes(preferredTime);
  if (!dateParts || minutes === null) return null;
  const desiredHour = Math.floor(minutes / 60);
  const desiredMinute = minutes % 60;
  const desiredAsUtcMs = Date.UTC(
    dateParts.year,
    dateParts.month - 1,
    dateParts.day,
    desiredHour,
    desiredMinute,
    0
  );

  let utcGuess = desiredAsUtcMs;
  for (let pass = 0; pass < 4; pass += 1) {
    const rendered = getLosAngelesDateTimeParts(new Date(utcGuess));
    if (!rendered) return null;
    const renderedAsUtcMs = Date.UTC(
      rendered.year,
      rendered.month - 1,
      rendered.day,
      rendered.hour,
      rendered.minute,
      0
    );
    const diff = desiredAsUtcMs - renderedAsUtcMs;
    if (diff === 0) break;
    utcGuess += diff;
  }

  const finalDate = new Date(utcGuess);
  const check = getLosAngelesDateTimeParts(finalDate);
  if (
    !check ||
    check.year !== dateParts.year ||
    check.month !== dateParts.month ||
    check.day !== dateParts.day ||
    check.hour !== desiredHour ||
    check.minute !== desiredMinute
  ) {
    return null;
  }

  return finalDate;
};

export const getPickupBookingStartIso = (
  rawValue?: string | null,
  fallback = "2026-03-04"
) => {
  const parsed = parseIsoDateParts(rawValue ?? "");
  if (parsed) return formatIsoDate(parsed);
  const fallbackParsed = parseIsoDateParts(fallback);
  if (fallbackParsed) return formatIsoDate(fallbackParsed);
  return "2026-03-04";
};

/**
 * Customer-blocked pickup dates (LA, YYYY-MM-DD). Used when we close public
 * booking for a service window (e.g. an invite-only private order week). These
 * dates are hidden from the customer date picker AND rejected by customer-side
 * validation. Admins are NOT affected (forAdmin bypasses), so they can still
 * create/reschedule pickups for invited customers. Already-booked pickups are
 * unaffected (this only gates NEW customer bookings). Remove dates here to
 * reopen them; past dates are harmless to leave.
 */
export const PICKUP_BLACKOUT_DATES = new Set<string>([
  "2026-06-09",
  "2026-06-10",
]);

/**
 * Validate a pickup window. Accepts a schedule to check against.
 * If no schedule is provided, falls back to Tuesday-only logic.
 *
 * Pass `forAdmin: true` to bypass the default customer pickup run and the
 * Tuesday-only fallback - admins can book any slot in
 * ADMIN_TIME_SLOTS on any weekday.
 */
export const isValidPickupWindowInLosAngeles = (
  preferredDate?: string | null,
  preferredTime?: string | null,
  bookingStartIso?: string | null,
  now = new Date(),
  schedule?: PickupScheduleEntry[],
  forAdmin = false,
) => {
  if (!preferredDate || !preferredTime) return false;
  // Customer booking is closed on blackout dates (invite-only weeks). Admins
  // (forAdmin) bypass so they can still create pickups for invited customers.
  if (!forAdmin && PICKUP_BLACKOUT_DATES.has(preferredDate)) return false;
  const slotMinutes = parseTimeToMinutes(preferredTime);
  if (slotMinutes === null) return false;
  if (!forAdmin && bookingStartIso && preferredDate < bookingStartIso) return false;

  const nowInfo = getLosAngelesNowInfo(now);
  if (!nowInfo) return false;
  if (preferredDate < nowInfo.isoDate) return false;
  if (preferredDate === nowInfo.isoDate && slotMinutes < nowInfo.minutes + PICKUP_MIN_NOTICE_MINUTES) return false;

  // Admins can confirm custom minute values, not just the suggested picker slots.
  if (!forAdmin) {
    const customerFloor = parseTimeToMinutes(CUSTOMER_MIN_PICKUP_TIME) ?? 18 * 60;
    if (!schedule && slotMinutes < customerFloor) return false;

    // Check against schedule if provided
    if (schedule) {
      const entry = schedule.find((e) => e.date === preferredDate);
      if (!entry) return false;
      if (!entry.time_slots.includes(preferredTime)) return false;
    } else {
      // Fallback: Tuesday's 6 PM run. Explicit schedules remain authoritative.
      const weekday = getIsoWeekday(preferredDate);
      if (weekday !== PICKUP_WEEKDAY) return false;
      if (!(DEFAULT_PICKUP_TIME_SLOTS as readonly string[]).includes(preferredTime)) return false;
    }
  }

  const pickupDateTime = toLosAngelesDateTime(preferredDate, preferredTime);
  if (!pickupDateTime) return false;
  return pickupDateTime.getTime() - now.getTime() >= PICKUP_MIN_NOTICE_MINUTES * 60_000;
};

/**
 * Get available time slots for a specific date.
 * Uses schedule entries if provided, otherwise falls back to Tuesday-only.
 *
 * Pass `forAdmin: true` to skip the default customer pickup run and to use
 * ADMIN_TIME_SLOTS as the per-day universe regardless of weekday.
 */
export const getAvailablePickupTimesForDateInLosAngeles = (
  dateValue: string,
  slots: readonly string[],
  bookingStartIso?: string | null,
  now = new Date(),
  schedule?: PickupScheduleEntry[],
  forAdmin = false,
) => {
  if (!forAdmin && bookingStartIso && dateValue < bookingStartIso) return [] as string[];

  // Determine which time slots this date uses
  let dateSlots: readonly string[];
  if (forAdmin) {
    // Admin gets the broader slot universe on any weekday, regardless of
    // schedule entries. Schedule data is a customer-surface convenience,
    // not an admin constraint.
    dateSlots = ADMIN_TIME_SLOTS;
  } else if (schedule) {
    const entry = schedule.find((e) => e.date === dateValue);
    if (!entry) return [] as string[];
    dateSlots = entry.time_slots;
  } else {
    // Fallback: Tuesday only
    const weekday = getIsoWeekday(dateValue);
    if (weekday !== PICKUP_WEEKDAY) return [] as string[];
    dateSlots = slots.filter(slot => (DEFAULT_PICKUP_TIME_SLOTS as readonly string[]).includes(slot));
  }

  const nowInfo = getLosAngelesNowInfo(now);
  if (!nowInfo) return [] as string[];
  if (dateValue < nowInfo.isoDate) return [] as string[];

  const customerFloorMinutes = forAdmin || schedule
    ? -1
    : (parseTimeToMinutes(CUSTOMER_MIN_PICKUP_TIME) ?? 18 * 60);

  const futureDate = dateValue > nowInfo.isoDate;
  return dateSlots.filter((slot) => {
    const minutes = parseTimeToMinutes(slot);
    if (minutes === null) return false;
    if (minutes < customerFloorMinutes) return false;
    if (futureDate) return true;
    return minutes >= nowInfo.minutes + PICKUP_MIN_NOTICE_MINUTES;
  });
};

/**
 * Get upcoming pickup dates from the authoritative calendar, or default Tuesdays
 * only when no calendar was supplied.
 */
export const getUpcomingPickupDatesInLosAngeles = ({
  now = new Date(),
  bookingStartIso,
  slots,
  daysAhead = 31,
  schedule,
}: {
  now?: Date;
  bookingStartIso?: string | null;
  slots: readonly string[];
  daysAhead?: number;
  schedule?: PickupScheduleEntry[];
}) => {
  const nowInfo = getLosAngelesNowInfo(now);
  if (!nowInfo) return [] as string[];
  const limitIso = addDaysToIsoDate(nowInfo.isoDate, daysAhead);
  if (!limitIso) return [] as string[];

  let startIso = nowInfo.isoDate;
  if (bookingStartIso && bookingStartIso > startIso) {
    startIso = bookingStartIso;
  }

  const dateSet = new Set<string>();

  // If we have a schedule from the DB, use those dates
  if (schedule) {
    for (const entry of schedule) {
      if (entry.date < startIso || entry.date >= limitIso) continue;
      const hasFutureSlot = getAvailablePickupTimesForDateInLosAngeles(entry.date, slots, bookingStartIso, now, schedule).length > 0;
      if (hasFutureSlot) dateSet.add(entry.date);
    }
    // A loaded empty calendar means closed, never fall back to recurring days.
    return [...dateSet].filter(d => !PICKUP_BLACKOUT_DATES.has(d)).sort();
  }

  // Always add default Tuesdays (schedule entries override if same date)
  const startWeekday = getIsoWeekday(startIso);
  if (startWeekday !== null) {
    const firstOffset = (PICKUP_WEEKDAY - startWeekday + 7) % 7;
    let cursorIso = addDaysToIsoDate(startIso, firstOffset);
    while (cursorIso && cursorIso < limitIso) {
      // Only add Tuesday if not already overridden by a schedule entry
      if (!dateSet.has(cursorIso)) {
        const hasFutureSlot =
          cursorIso !== nowInfo.isoDate ||
          slots.some((slot) => {
            const minutes = parseTimeToMinutes(slot);
            return minutes !== null && minutes >= nowInfo.minutes + PICKUP_MIN_NOTICE_MINUTES;
          });
        if (hasFutureSlot) dateSet.add(cursorIso);
      }
      const nextCursor = addDaysToIsoDate(cursorIso, 7);
      if (!nextCursor) break;
      cursorIso = nextCursor;
    }
  }

  // Hide blackout dates from the customer picker (invite-only weeks).
  return [...dateSet].filter((d) => !PICKUP_BLACKOUT_DATES.has(d)).sort();
};

export const parseScheduledPickupDateTimeInLosAngeles = (pickup: {
  preferred_date?: string | null;
  preferred_time?: string | null;
  confirmed_pickup_time?: string | null;
}) => {
  if (pickup.confirmed_pickup_time) {
    const confirmedMatch = pickup.confirmed_pickup_time
      .trim()
      .match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(?::\d{2})?$/);
    if (confirmedMatch) {
      const fromConfirmed = toLosAngelesDateTime(
        confirmedMatch[1],
        confirmedMatch[2]
      );
      if (fromConfirmed) return fromConfirmed;
    } else {
      const fallback = new Date(pickup.confirmed_pickup_time);
      if (!Number.isNaN(fallback.getTime())) return fallback;
    }
  }
  return toLosAngelesDateTime(pickup.preferred_date, pickup.preferred_time);
};

/**
 * Pickups run on Tuesdays only. Every path that sets a pickup date (customer
 * booking, schedule entries, admin reschedules) checks this, so a stray
 * non-Tuesday schedule row or reschedule can never create a mid-week pickup.
 */
export const isPickupWeekday = (isoDate: string | null | undefined): boolean =>
  !!isoDate && getIsoWeekday(isoDate) === PICKUP_WEEKDAY;
export const PICKUP_WEEKDAY_ERROR = "Pickups run on Tuesdays only. Choose a Tuesday.";
